import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle, XCircle, AlertTriangle, FileText, Printer, Copy, ExternalLink } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

interface NfceEmissionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isEmitting: boolean;
  result: {
    success: boolean;
    status: "authorized" | "rejected" | "pending" | "error";
    nfce_id?: string;
    numero?: number;
    serie?: number;
    chave_acesso?: string;
    protocolo?: string;
    message?: string;
    error?: string;
    cstat?: string;
    xmotivo?: string;
    printed?: boolean;
    print_error?: string;
  } | null;
  onRetry?: () => void;
  onPrint?: () => void;
}

// Mapeamento de dicas para códigos de rejeição comuns
const REJECTION_TIPS: Record<string, string> = {
  "531": "Verifique se as regras tributárias estão configuradas corretamente para os produtos.",
  "539": "O CST/CSOSN do item está incompatível com a operação. Revise as regras fiscais.",
  "225": "Data de emissão inválida. Verifique o horário do servidor.",
  "204": "Chave de acesso duplicada. Aguarde alguns minutos e tente novamente.",
  "215": "NFC-e já está autorizada com esta chave.",
  "301": "Dados do emitente incompletos. Preencha todos os campos obrigatórios em Fiscal > Emitente.",
  "302": "IE do emitente inválida. Verifique a Inscrição Estadual cadastrada.",
  "501": "CSC inválido. Configure o CSC corretamente em Fiscal > Configurações.",
  "999": "Erro interno da SEFAZ. Tente novamente em alguns minutos.",
};

export function NfceEmissionDialog({
  open,
  onOpenChange,
  isEmitting,
  result,
  onRetry,
  onPrint,
}: NfceEmissionDialogProps) {
  const formatChaveAcesso = (chave: string) => {
    // Formata a chave de acesso em grupos de 4 dígitos
    return chave.match(/.{1,4}/g)?.join(" ") || chave;
  };

  const copyChaveAcesso = () => {
    if (result?.chave_acesso) {
      navigator.clipboard.writeText(result.chave_acesso);
      toast.success("Chave de acesso copiada!");
    }
  };

  const getStatusBadge = () => {
    if (isEmitting) {
      return (
        <Badge variant="secondary" className="gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Emitindo...
        </Badge>
      );
    }

    switch (result?.status) {
      case "authorized":
        return (
          <Badge variant="default" className="bg-green-600 gap-1">
            <CheckCircle className="h-3 w-3" />
            Autorizada
          </Badge>
        );
      case "rejected":
        return (
          <Badge variant="destructive" className="gap-1">
            <XCircle className="h-3 w-3" />
            Rejeitada
          </Badge>
        );
      case "pending":
        return (
          <Badge variant="secondary" className="gap-1">
            <AlertTriangle className="h-3 w-3" />
            Pendente
          </Badge>
        );
      default:
        return (
          <Badge variant="destructive" className="gap-1">
            <XCircle className="h-3 w-3" />
            Erro
          </Badge>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Emissão de NFC-e
          </DialogTitle>
          <DialogDescription>
            {isEmitting
              ? "Aguarde enquanto a nota está sendo emitida..."
              : result?.success
              ? "Nota fiscal emitida com sucesso!"
              : "Detalhes da emissão"
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Status:</span>
            {getStatusBadge()}
          </div>

          {/* Loading */}
          {isEmitting && (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                Comunicando com a SEFAZ...
              </p>
            </div>
          )}

          {/* Resultado - Sucesso */}
          {!isEmitting && result?.success && (
            <>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Número:</span>
                  <p className="font-medium">{result.numero}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Série:</span>
                  <p className="font-medium">{result.serie}</p>
                </div>
              </div>

              {result.chave_acesso && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Chave de Acesso:</span>
                    <Button variant="ghost" size="sm" onClick={copyChaveAcesso}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs font-mono bg-muted p-2 rounded break-all">
                    {formatChaveAcesso(result.chave_acesso)}
                  </p>
                </div>
              )}

              {result.protocolo && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Protocolo:</span>
                  <p className="font-medium">{result.protocolo}</p>
                </div>
              )}

              {/* Status de Impressão */}
              {result.printed === true && (
                <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-700 dark:text-green-400">
                    DANFE enviado para impressora
                  </span>
                </div>
              )}

              {result.printed === false && result.print_error && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                        Impressão falhou
                      </p>
                      <p className="text-xs text-amber-600 dark:text-amber-300">
                        {result.print_error}
                      </p>
                    </div>
                  </div>
                  {onPrint && (
                    <Button variant="outline" size="sm" onClick={onPrint} className="w-full">
                      <Printer className="h-4 w-4 mr-2" />
                      Tentar Imprimir Novamente
                    </Button>
                  )}
                </div>
              )}

              {/* QR Code */}
              {result.chave_acesso && (
                <div className="flex justify-center py-4">
                  <div className="p-4 bg-white rounded-lg">
                    <QRCodeSVG
                      value={`https://www.nfce.fazenda.sp.gov.br/NFCeConsultaPublica/Paginas/ConsultaQRCode.aspx?chNFe=${result.chave_acesso}`}
                      size={150}
                      level="M"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                {onPrint && result.printed !== true && (
                  <Button variant="outline" className="flex-1" onClick={onPrint}>
                    <Printer className="h-4 w-4 mr-2" />
                    Imprimir
                  </Button>
                )}
                <Button variant="default" className="flex-1" onClick={() => onOpenChange(false)}>
                  Fechar
                </Button>
              </div>
            </>
          )}

          {/* Resultado - Erro/Rejeição */}
          {!isEmitting && !result?.success && result && (
            <>
              {/* Código de Rejeição */}
              {result.cstat && (
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <span className="text-sm text-muted-foreground">Código SEFAZ:</span>
                    <p className="font-mono font-bold text-lg">{result.cstat}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-destructive" />
                </div>
              )}

              {/* Mensagem de Erro */}
              <div className="p-4 bg-destructive/10 rounded-lg space-y-2">
                <p className="text-sm font-medium text-destructive">
                  {result.xmotivo || result.message || result.error || "Erro desconhecido na emissão"}
                </p>
              </div>

              {/* Dica de Resolução */}
              {result.cstat && REJECTION_TIPS[result.cstat] && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Dica</p>
                      <p className="text-sm text-amber-600 dark:text-amber-300">
                        {REJECTION_TIPS[result.cstat]}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                {onRetry && (
                  <Button variant="outline" className="flex-1" onClick={onRetry}>
                    Tentar Novamente
                  </Button>
                )}
                <Button variant="default" className="flex-1" onClick={() => onOpenChange(false)}>
                  Fechar
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
